import React, { useEffect, useState } from 'react';
import { Button, Col, Divider, message, Modal, Row, Select, Spin } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'lucide-react';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

type ProductiveUser = {
  value: string;
  label: string;
  originalLabel: string;
  email?: string;
};

type ProductiveUserResponse = Omit<ProductiveUser, 'originalLabel'>;

type BugherdUser = {
  id: string | number;
  display_name?: string;
  name?: string;
  email?: string;
};

type Mapping = {
  productiveUserId: string;
  bugherdUserId: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  integrationId: string | null;
  projectIntegrationId: string | null;
  productiveProjectId: string | null;
  bugherdUsers: BugherdUser[];
  container?: HTMLElement | (() => HTMLElement);
}

const emptyMapping: Mapping = {
  productiveUserId: '',
  bugherdUserId: ''
};

const ProductiveUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  productiveProjectId,
  bugherdUsers,
  container
}) => {
  const [loading, setLoading] = useState(false);
  const [productiveUsers, setProductiveUsers] = useState<ProductiveUser[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([emptyMapping]);

  useEffect(() => {
    if (open && integrationId && productiveProjectId) loadData();
  }, [open, integrationId, productiveProjectId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchProductiveUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchProductiveUsers = async () => {
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/productive_users?id=${integrationId}&productive_project_id=${productiveProjectId}`
      );
      setProductiveUsers(
        results.map((user: ProductiveUserResponse) => ({
          ...user,
          label: user.email ? `${user.label} (${user.email})` : user.label,
          originalLabel: user.label
        }))
      );
    } catch (error) {
      message.error('Failed to fetch Productive project users.');
    }
  };

  const fetchCurrentSettings = async () => {
    if (!projectIntegrationId) {
      setMappings([emptyMapping]);
      return;
    }

    try {
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      const existing = Object.entries(
        result?.settings?.user_mapping || {}
      ).map(([productiveUserId, bugherdUserId]) => ({
        productiveUserId,
        bugherdUserId: String(bugherdUserId)
      }));
      setMappings(existing.length ? existing : [emptyMapping]);
    } catch (error) {
      message.error('Failed to fetch current user mapping.');
      setMappings([emptyMapping]);
    }
  };

  const saveMappings = async (currentMappings: Mapping[]) => {
    if (!projectIntegrationId) {
      message.error(
        'Unable to save user mappings until the Productive integration settings have been saved.'
      );
      return;
    }

    const usedProductiveUserIds = new Set<string>();
    const usedBugherdUserIds = new Set<string>();
    const userMapping = currentMappings.reduce((mapping, pair) => {
      if (
        pair.productiveUserId &&
        pair.bugherdUserId &&
        !usedProductiveUserIds.has(pair.productiveUserId) &&
        !usedBugherdUserIds.has(pair.bugherdUserId)
      ) {
        mapping[pair.productiveUserId] = pair.bugherdUserId;
        usedProductiveUserIds.add(pair.productiveUserId);
        usedBugherdUserIds.add(pair.bugherdUserId);
      }
      return mapping;
    }, {} as Record<string, string>);

    try {
      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Productive`,
        {
          project_integration_id: projectIntegrationId,
          settings: { user_mapping: userMapping }
        }
      );
    } catch (error) {
      message.error('Failed to save user mappings.');
    }
  };

  const handleAutomap = () => {
    const autoPairs: Mapping[] = [];
    const usedBugherdUserIds = new Set<string>();

    productiveUsers.forEach(productiveUser => {
      const remoteLabel = productiveUser.originalLabel.toLowerCase();
      const matchedBugherdUser = bugherdUsers.find(
        user =>
          (productiveUser.email &&
            user.email?.toLowerCase() === productiveUser.email.toLowerCase()) ||
          user.display_name?.toLowerCase() === remoteLabel ||
          user.name?.toLowerCase() === remoteLabel ||
          user.email?.toLowerCase() === remoteLabel
      );

      if (
        matchedBugherdUser &&
        !usedBugherdUserIds.has(String(matchedBugherdUser.id))
      ) {
        autoPairs.push({
          productiveUserId: productiveUser.value,
          bugherdUserId: String(matchedBugherdUser.id)
        });
        usedBugherdUserIds.add(String(matchedBugherdUser.id));
      }
    });

    if (autoPairs.length) {
      setMappings(autoPairs);
      saveMappings(autoPairs);
      message.success(`Successfully mapped ${autoPairs.length} users.`);
    } else {
      message.info('No matching users found by name or email.');
    }
  };

  const handleMappingChange = (
    index: number,
    field: keyof Mapping,
    value: string
  ) => {
    const newMappings = mappings.map((mapping, currentIndex) =>
      currentIndex === index ? { ...mapping, [field]: value || '' } : mapping
    );
    setMappings(newMappings);

    if (
      newMappings[index].productiveUserId &&
      newMappings[index].bugherdUserId
    ) {
      saveMappings(newMappings);
    }
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter(
      (_mapping, currentIndex) => currentIndex !== index
    );
    setMappings(newMappings);
    saveMappings(newMappings);
  };

  const bugherdUserOptions = bugherdUsers.map(user => ({
    label: user.display_name || user.name || user.email,
    value: String(user.id)
  }));

  const availableOptions = (
    index: number,
    field: keyof Mapping,
    options: { value: string; label?: string }[]
  ) => {
    const selectedIds = mappings
      .filter((_mapping, currentIndex) => currentIndex !== index)
      .map(mapping => mapping[field])
      .filter(Boolean);

    return options.filter(option => !selectedIds.includes(option.value));
  };

  return (
    <Modal
      title="Manage users between BugHerd and Productive"
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      getContainer={container}
    >
      <div className={styles.modalContent}>
        <p>
          Match users so BugHerd task assignments and Productive comment
          authors are synced to the correct people.
        </p>

        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : (
          <div className={styles.mappingList}>
            <div className={styles.mappingRowsContainer}>
              <Row gutter={16} className={styles.headerRow}>
                <Col span={10}>
                  <strong>BugHerd User</strong>
                </Col>
                <Col span={2}></Col>
                <Col span={10}>
                  <strong>Productive User</strong>
                </Col>
                <Col span={2}></Col>
              </Row>
              {mappings.map((mapping, index) => (
                <Row
                  key={index}
                  gutter={16}
                  className={styles.mappingRow}
                  align="middle"
                >
                  <Col span={10}>
                    <Select
                      showSearch
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Search BugHerd user"
                      optionFilterProp="label"
                      value={mapping.bugherdUserId || undefined}
                      options={availableOptions(
                        index,
                        'bugherdUserId',
                        bugherdUserOptions
                      )}
                      onChange={value =>
                        handleMappingChange(index, 'bugherdUserId', value)
                      }
                    />
                  </Col>
                  <Col span={2} style={{ textAlign: 'center' }}>
                    <Link size={16} className={styles.linkIcon} />
                  </Col>
                  <Col span={10}>
                    <Select
                      showSearch
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Search Productive user"
                      optionFilterProp="label"
                      value={mapping.productiveUserId || undefined}
                      options={availableOptions(
                        index,
                        'productiveUserId',
                        productiveUsers
                      )}
                      onChange={value =>
                        handleMappingChange(index, 'productiveUserId', value)
                      }
                    />
                  </Col>
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => handleRemoveMapping(index)}
                    />
                  </Col>
                </Row>
              ))}
              <div className={styles.addMapping}>
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setMappings([...mappings, { ...emptyMapping }])
                  }
                >
                  Sync another user
                </Button>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Divider plain>or</Divider>
              <Button
                type="primary"
                size="large"
                style={{ padding: '0 40px' }}
                onClick={handleAutomap}
              >
                Automap BugHerd to Productive users
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ProductiveUserMappingModal;
